import React from "react";
import {
  Card,
  Typography,
  Descriptions,
  Space,
  Skeleton,
  Tag,
  theme,
} from "antd";
import {
  MailOutlined,
  PhoneOutlined,
  GlobalOutlined,
  TranslationOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import { useLanguage } from "src/providers/LanguageProvider";
import { useSelector } from "react-redux";
import useFormatDate from "src/hooks/useFormatDate";

type Account = {
  Id?: string | number;
  FirstName?: string;
  LastName?: string;
  Email?: string;
  Phone?: string;
  Country?: string;
  Language?: string;
  CreatedAt?: string;
  UpdatedAt?: string;
  IsConfirm?: boolean;
  IsActive?: boolean;
};

const ProfileSummary: React.FC<{ account?: Account; labelWidth?: number }> = ({
  account,
  labelWidth = 160,
}) => {
  const { t } = useLanguage();
  const { token } = theme.useToken();
  const profile = ((useSelector((s: any) => s.auth.account) as Account | undefined));

  const formatDate = useFormatDate();

  const labelStyle: React.CSSProperties = {
    width: labelWidth,
    color: token.colorTextSecondary,
    background: "transparent",
  };
  const contentStyle: React.CSSProperties = {
    color: token.colorText,
    fontWeight: 500,
  };

  if (!profile) {
    return (
      <Card className="card" style={{ borderRadius: 14, height: "100%" }}>
        <Skeleton active paragraph={{ rows: 8 }} />
      </Card>
    );
  }

  return (
    <Card
      className="card"
      style={{ borderRadius: 14, height: "100%" }}
      title={
        <Typography.Title level={5} style={{ margin: 0 }}>
          {t("PROFILE_SUMMARY")}
        </Typography.Title>
      }
      extra={
        <Space size={8} wrap>
          {profile.IsConfirm ? (
            <Tag color="blue" icon={<CheckCircleFilled />}>
              {t("PROFILE_CONFIRMED")}
            </Tag>
          ) : (
            <Tag>{t("PROFILE_UNVERIFIED")}</Tag>
          )}
          <Tag color={profile.IsActive ? "green" : "red"}>
            {profile.IsActive ? t("STATUS_ACTIVE") : t("STATUS_PASSIVE")}
          </Tag>
        </Space>
      }
    >
      <Descriptions column={1} colon labelStyle={labelStyle} contentStyle={contentStyle}>
        <Descriptions.Item label={t("LABEL_ID")}>
          {profile.Id ? (
            <Typography.Text copyable={{ text: String(profile.Id) }}>
              {String(profile.Id)}
            </Typography.Text>
          ) : (
            "-"
          )}
        </Descriptions.Item>

        <Descriptions.Item label={t("LABEL_FIRSTNAME")}>
          {profile.FirstName || "-"}
        </Descriptions.Item>

        <Descriptions.Item label={t("LABEL_LASTNAME")}>
          {profile.LastName || "-"}
        </Descriptions.Item>

        <Descriptions.Item label={t("LABEL_EMAIL")}>
          {profile.Email ? (
            <Space>
              <MailOutlined />
              <a href={`mailto:${profile.Email}`} style={{ color: token.colorText }}>
                {profile.Email}
              </a>
            </Space>
          ) : (
            "-"
          )}
        </Descriptions.Item>

        <Descriptions.Item label={t("LABEL_PHONE")}>
          {profile.Phone ? (
            <Space>
              <PhoneOutlined />
              <a href={`tel:${profile.Phone}`} style={{ color: token.colorText }}>
                {profile.Phone}
              </a>
            </Space>
          ) : (
            "-"
          )}
        </Descriptions.Item>

        <Descriptions.Item label={t("LABEL_COUNTRY")}>
          {profile.Country ? (
            <Space>
              <GlobalOutlined />
              <Typography.Text>{profile.Country}</Typography.Text>
            </Space>
          ) : (
            "-"
          )}
        </Descriptions.Item>

        <Descriptions.Item label={t("LABEL_LANGUAGE")}>
          {profile.Language ? (
            <Space>
              <TranslationOutlined />
              <Typography.Text>{profile.Language}</Typography.Text>
            </Space>
          ) : (
            "-"
          )}
        </Descriptions.Item>

        <Descriptions.Item label={t("CREATED_AT")}>
          {formatDate(profile.CreatedAt)}
        </Descriptions.Item>

        <Descriptions.Item label={t("UPDATED_AT")}>
          {formatDate(profile.UpdatedAt)}
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );
};

export default ProfileSummary;
